/**
 * Branch Redux Slice
 * State management untuk Branch (Cabang)
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as branchService from '../../services/branchService';
import { Branch, BranchCreate, BranchUpdate, BranchListParams } from '../../types/branch';

interface BranchState {
  branches: Branch[];
  currentBranch: Branch | null;
  selectedBranchId: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: BranchState = {
  branches: [],
  currentBranch: null,
  selectedBranchId: null,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchBranchesAsync = createAsyncThunk(
  'branch/fetchBranches',
  async (params?: BranchListParams, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranches(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch branches');
    }
  }
);

export const fetchBranchByIdAsync = createAsyncThunk(
  'branch/fetchBranchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await branchService.getBranchById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch branch');
    }
  }
);

export const createBranchAsync = createAsyncThunk(
  'branch/createBranch',
  async (branchData: BranchCreate, { rejectWithValue }) => {
    try {
      const response = await branchService.createBranch(branchData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create branch');
    }
  }
);

export const updateBranchAsync = createAsyncThunk(
  'branch/updateBranch',
  async ({ id, data }: { id: number; data: BranchUpdate }, { rejectWithValue }) => {
    try {
      const response = await branchService.updateBranch(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update branch');
    }
  }
);

export const deleteBranchAsync = createAsyncThunk(
  'branch/deleteBranch',
  async (id: number, { rejectWithValue }) => {
    try {
      await branchService.deleteBranch(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete branch');
    }
  }
);

const branchSlice = createSlice({
  name: 'branch',
  initialState,
  reducers: {
    setSelectedBranch(state, action: PayloadAction<number | null>) {
      state.selectedBranchId = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    clearCurrentBranch(state) {
      state.currentBranch = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Branches
      .addCase(fetchBranchesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranchesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload;
      })
      .addCase(fetchBranchesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Branch By ID
      .addCase(fetchBranchByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranchByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBranch = action.payload;
      })
      .addCase(fetchBranchByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create Branch
      .addCase(createBranchAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBranchAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.branches.push(action.payload);
      })
      .addCase(createBranchAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update Branch
      .addCase(updateBranchAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBranchAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.currentBranch?.id === action.payload.id) {
          state.currentBranch = action.payload;
        }
      })
      .addCase(updateBranchAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete Branch
      .addCase(deleteBranchAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBranchAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = state.branches.filter(b => b.id !== action.payload);
      })
      .addCase(deleteBranchAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedBranch, clearError, clearCurrentBranch } = branchSlice.actions;

export default branchSlice.reducer;
